import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { FC } from 'react'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import {
  asRecommendationStatus,
  getNetworkPartnerDisplayName,
  MEMBER_SERVICE_LEVEL,
  RECOMMENDATION_STATUS,
  Steerage,
  SteerageProvider,
  SteerageProviderValidationResult,
  SteerageProviderWaiverLevel,
  useDeleteProviderFromSteerage,
  useEditSteerageProviderWaiverLevel,
  useSelectSteerageProvider,
  useUpdateSteerageProviderPriorAuthorization,
  useValidateSteerageProvider,
  type EditSteerageProviderWaiverLevelPayload,
  type RecommendationStatus,
} from '~/entities/steerage'
import { useStates } from '~/entities/state'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Modal,
  Radio,
  Skeleton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { EditField, Loader } from '~/shared/ui'
import waiver_level from '~/assets/images/waiver_level.svg'
import PermContactCalendar from '@mui/icons-material/PermContactCalendar'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SteerageProviderForm from './SteerageProviderForm'
import { EditProviderAndAvailabilityPayload, logEvent } from '~/shared/lib/events'
import { formatApiDate } from '~/shared/lib/date'
import {
  referralSteerage,
  referralSteerageProviders,
  type ReferralSteerageReasonForExceptionListResponse,
} from '@fireflyhealth/api'
import {
  UpdateProviderAvailabilityModal,
  type ProviderDetailWithAdditionalData,
} from '~/features/member-profile/care-navigation/provider-search'
import { invalidateCachedCaseData } from '~/shared/lib/queryKeys'
import PriorAuthorizationSelect from './PriorAuthorizationSelect'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

const PRIOR_AUTH_ID_LENGTH = 12

/** An unrated provider and an explicitly neutral one read the same to a clinician. */
const isNeutralRating = (status: RecommendationStatus | null) =>
  status === RECOMMENDATION_STATUS.NO_RATING || status === RECOMMENDATION_STATUS.NEUTRAL

interface SteerageProviderListItemProps {
  steerage: Steerage
  provider: SteerageProvider
  setShowEditProviderForm: (providerId: number | null) => void
  showEditProviderForm: number | null
  showAddProviderForm: boolean
  setShowAddProviderForm: (showAddProviderForm: boolean) => void
  validReasonsForException: ReferralSteerageReasonForExceptionListResponse
  memberSelectedProvider: number | null
  setMemberSelectedProvider: (providerId: number | null) => void
  isWaiverToggleEnabled?: boolean | null
  priorAuthorizationRequired?: boolean
  onSaveSteerage?: () => Promise<void>
}
const editProviderModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '16px',
  width: '490px',
}

const INLINE_ICON_SX = { mb: '-6px' }

const SteerageProviderListItem: FC<SteerageProviderListItemProps> = (
  props: SteerageProviderListItemProps,
) => {
  const client = useQueryClient()
  const provider = props?.provider
  const steerage = props?.steerage
  const availability = props?.provider?.live_availability
  const verifiedOn = formatApiDate(availability?.added_on, 'MMM d, yyyy')
  const showEditProviderForm = props?.showEditProviderForm

  const { mutateAsync: handleDeleteProviderFromSteerage } = useDeleteProviderFromSteerage()
  const { data: states } = useStates()

  //  API for updating the waiver level
  const { isPending: isEditingWaiverLevel, mutate: handleEditSteerageProviderWaiverLevel } =
    useEditSteerageProviderWaiverLevel()

  // API for updating per-provider prior authorization
  const { mutateAsync: handleUpdateProviderPriorAuthorization } =
    useUpdateSteerageProviderPriorAuthorization()

  const [paSystemIdInput, setPaSystemIdInput] = useState<string>(
    () =>
      provider?.prior_authorization_system_id ||
      provider?.prior_authorization?.originator_system_id ||
      '',
  )
  const lastSavedPaSystemIdRef = useRef<string>(paSystemIdInput)

  const paSystemIdError =
    paSystemIdInput.length > 0 && paSystemIdInput.length !== PRIOR_AUTH_ID_LENGTH
      ? `Must be ${PRIOR_AUTH_ID_LENGTH} characters long`
      : undefined

  const savePaSystemId = async (value: string) => {
    if (lastSavedPaSystemIdRef.current === value) return
    const previouslySaved = lastSavedPaSystemIdRef.current
    lastSavedPaSystemIdRef.current = value
    try {
      // Persist any pending steerage form edits first; otherwise the steerage
      // invalidation below would refetch and re-initialize the parent Formik form,
      // wiping the user's unsaved changes.
      if (props.onSaveSteerage) await props.onSaveSteerage()
      await handleUpdateProviderPriorAuthorization({
        steerage_id: steerage.id,
        id: provider.id,
        prior_authorization_system_id: value,
      })
      await client.cancelQueries({
        queryKey: referralSteerageProviders.listQueryKey({ steerage_id: steerage.id }),
      })
      await client.invalidateQueries({
        queryKey: referralSteerageProviders.listQueryKey({ steerage_id: steerage.id }),
      })
      await client.cancelQueries({
        queryKey: referralSteerage.retrieveQueryKey({ id: steerage.id }),
      })
      await client.invalidateQueries({
        queryKey: referralSteerage.retrieveQueryKey({ id: steerage.id }),
      })
    } catch (e) {
      // Restore the dedupe guard: a failed save must not look saved, or re-blurring the
      // same value returns early and drops it silently.
      lastSavedPaSystemIdRef.current = previouslySaved
      throw e
    }
  }

  const handlePaSystemIdBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // onBlur bubbles from inner Autocomplete elements; ignore focus moves that stay inside.
    if (event.currentTarget.contains(event.relatedTarget)) return
    // Skip partial typed values so mid-typing blurs don't persist garbage. Dropdown picks
    // bypass this path (they save via onSelectOption) so non-12-char real IDs still save.
    if (paSystemIdInput.length > 0 && paSystemIdInput.length !== PRIOR_AUTH_ID_LENGTH) return
    fireAndForget('[Steerage] save prior-auth id', () => savePaSystemId(paSystemIdInput))
  }

  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null)
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    const providerIdAttribute = event.currentTarget.getAttribute('data-provider-id')
    setSelectedProviderId(providerIdAttribute ? Number(providerIdAttribute) : null)
    setAnchorEl(event.currentTarget)
  }
  // for opening and closing menu icon
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => {
    setAnchorEl(null)
  }

  //API's for selecting/unselecting the steerage provider on behalf of a member
  const { isPending: isSelectingProvider, mutate: handleSelectingSteerageProvider } =
    useSelectSteerageProvider()

  // For opening and closing Availability modal
  const [openAvailabilityModal, setOpenAvailabilityModal] = useState(false)

  // For expanding/collapsing TINs and Organization NPIs
  const [showAllTins, setShowAllTins] = useState(false)
  const [showAllOrganizationNpis, setShowAllOrganizationNpis] = useState(false)
  const handleOpenAvailabilityModal = () => {
    // Close the menu before opening the modal. Both install their own focus
    // trap on `document`; leaving the menu open keeps two traps active at once,
    // and they fight each other for focus indefinitely (matches
    // handleValidateProviderClick, which already closes the menu first).
    handleClose()
    setOpenAvailabilityModal(true)
    // Log the opening of the edit Availability modal to full story
    void logEvent<EditProviderAndAvailabilityPayload>('EDIT_AVAILABILITY_BUTTON_CLICK', {
      providerNpi: provider?.npi ?? null,
      careOrganisationName: provider?.care_organization_name ?? null,
      steerageId: steerage?.id,
      isEditedInDirectory: false,
      isProviderSearch: props?.provider?.npi ? true : false,
    })
  }
  const handleCloseAvailabilityModal = () => {
    setOpenAvailabilityModal(false)
    handleClose()
  }
  const isMemberInBenefitProgram = steerage?.member_service_level != MEMBER_SERVICE_LEVEL.CARE

  // For AI validation of provider
  const [validationResult, setValidationResult] = useState<SteerageProviderValidationResult | null>(
    null,
  )
  const [openValidationModal, setOpenValidationModal] = useState(false)
  const { isPending: isValidating, mutateAsync: handleValidateProvider } =
    useValidateSteerageProvider()

  const handleValidateProviderClick = async () => {
    handleClose()
    // Open modal immediately with loading state
    setValidationResult(null)
    setOpenValidationModal(true)
    try {
      const result = await handleValidateProvider({
        steerageId: steerage?.id,
        providerId: provider?.id,
      })
      setValidationResult(result)
    } catch (error) {
      // Error notification handled by the hook
      setOpenValidationModal(false)
    }
  }

  const handleCloseValidationModal = () => {
    setOpenValidationModal(false)
    setValidationResult(null)
  }

  // Helper function to render text with markdown links as clickable links
  const renderTextWithLinks = (text: string) => {
    // Match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index))
      }
      // Add the link
      const [, linkText, url] = match
      parts.push(
        <Link
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textDecoration: 'underline' }}
        >
          {linkText}
        </Link>,
      )
      lastIndex = match.index + match[0].length
    }

    // Add remaining text after last link
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex))
    }

    return parts.length > 0 ? parts : text
  }

  // Helper function to render TINs with truncation
  const renderTinsWithTruncation = (tins: string[]) => {
    const maxVisible = 2
    const visibleTins = showAllTins ? tins : tins.slice(0, maxVisible)
    const remainingCount = tins.length - maxVisible

    return (
      <Typography>
        {'TIN '}
        {visibleTins.join(', ')}
        {!showAllTins && remainingCount > 0 && (
          <>
            {' ... and '}
            <Link
              component="button"
              variant="body2"
              onClick={e => {
                e.preventDefault()
                setShowAllTins(true)
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {remainingCount} more
            </Link>
          </>
        )}
        {showAllTins && tins.length > maxVisible && (
          <>
            {' '}
            <Link
              component="button"
              variant="body2"
              onClick={e => {
                e.preventDefault()
                setShowAllTins(false)
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Show less
            </Link>
          </>
        )}
      </Typography>
    )
  }

  // Helper function to render Organization NPIs with truncation
  const renderOrganizationNpisWithTruncation = (npis: string[]) => {
    const maxVisible = 2
    const visibleNpis = showAllOrganizationNpis ? npis : npis.slice(0, maxVisible)
    const remainingCount = npis.length - maxVisible

    return (
      <Typography>
        {'Organization NPI '}
        {visibleNpis.join(', ')}
        {!showAllOrganizationNpis && remainingCount > 0 && (
          <>
            {' ... and '}
            <Link
              component="button"
              variant="body2"
              onClick={e => {
                e.preventDefault()
                setShowAllOrganizationNpis(true)
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {remainingCount} more
            </Link>
          </>
        )}
        {showAllOrganizationNpis && npis.length > maxVisible && (
          <>
            {' '}
            <Link
              component="button"
              variant="body2"
              onClick={e => {
                e.preventDefault()
                setShowAllOrganizationNpis(false)
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Show less
            </Link>
          </>
        )}
      </Typography>
    )
  }

  const isWaiverLevelDisabled = (provider: SteerageProvider) => {
    // Waiver level dropdown should only be enabled in below scenarios
    // 1. When the both provider npi and facility details are present
    // 2. When waiverLevel value is blank
    // 3. When the waiver level is not applicable
    if (!provider.waiver_level) return false
    if (provider.waiver_level && provider.waiver_level == 'Not Applicable') return false
    if (provider?.npi && provider?.care_organization_name) return false
    return true
  }

  const storeWaiverLevel = (
    value: string,
    providerId: EditSteerageProviderWaiverLevelPayload['id'],
  ) => {
    const payload: EditSteerageProviderWaiverLevelPayload = {
      id: providerId,
      steerage_id: props?.steerage?.id ?? 0,
      waiver_level: value
        ? (value as EditSteerageProviderWaiverLevelPayload['waiver_level'])
        : null,
    }

    handleEditSteerageProviderWaiverLevel(payload, {
      onSuccess: () =>
        fireAndForget('[Steerage] refresh after waiver-level change', async () => {
          await client.cancelQueries({
            queryKey: referralSteerageProviders.listQueryKey({
              steerage_id: props?.steerage?.id ?? 0,
            }),
          })
          await client.invalidateQueries({
            queryKey: referralSteerageProviders.listQueryKey({
              steerage_id: props?.steerage?.id ?? 0,
            }),
          })
        }),
    })
  }

  let defaultWaiverLevel = provider.waiver_level

  if (!defaultWaiverLevel) {
    if (provider.recommendation_status === 'RECOMMENDED') {
      if (provider.npi) {
        defaultWaiverLevel = 'Provider'
      } else if (provider.care_organization_name) {
        defaultWaiverLevel = 'Facility'
      } else {
        defaultWaiverLevel = 'Not Applicable'
      }
    } else {
      defaultWaiverLevel = 'Not Applicable'
    }
  }

  const updateAvailabilityProviderObject = () => {
    const providerData: ProviderDetailWithAdditionalData = {
      id: null,
      npi: provider?.npi?.toString() ?? '',
      unique_identifier: '',
      first_name: provider?.first_name ?? null,
      last_name: provider?.last_name ?? null,
      middle_name: null,
      name:
        (provider?.first_name || provider?.last_name
          ? provider?.first_name + ' ' + provider?.last_name
          : provider?.care_organization_name) ?? null,
      gender: null,
      specialties: [],
      languages: null,
      specialty_groups: null,
      location_types: null,
      provider_type: provider?.provider_type ?? null,
      recommendation_status: null,
      recommendation_reason_labels: null,
      composite_score: null,
      firefly_recommendation_factors: null,
      talon_recommendation_factors: null,
      partnership: null,
      locations: [
        {
          unique_identifier: '',
          name: provider?.care_organization_name ? provider?.care_organization_name : null,
          address_details: {
            street: null,
            address_line_1: provider?.address_line_1 ?? '',
            address_line_2: provider?.address_line_2 ?? '',
            city: provider?.city ?? '',
            state: provider?.state?.abbreviation ? provider?.state?.abbreviation : '',
            zip: provider?.zip_code?.toString() ?? '',
          },
          phone_numbers: null,
          fax_numbers: null,
          availability: provider?.live_availability
            ? {
                added_on: provider.live_availability.added_on,
                days_till_next_availability: provider.live_availability.days_till_next_availability,
                specialty_group: provider.live_availability.specialty_group,
                does_provider_exist: provider.live_availability.does_provider_exist,
              }
            : null,
          confidence: null,
          network_partner_agreement_type: null,
          location_in_network: null,
          distance: null,
          is_verified: null,
          partner_name: null,
          tins: null,
          organization_npis: null,
          talon_associated_procedure_prices: null,
          geo_code: null,
        },
      ],
    }
    return providerData
  }

  const getRecommendationStatusElementForProvider = (provider: SteerageProvider) => {
    const recommendationStatus = asRecommendationStatus(provider?.recommendation_status)

    return (
      <Box sx={{ display: 'inline-block', alignItems: 'center', ml: 1, color: 'grey.300' }}>
        {recommendationStatus === RECOMMENDATION_STATUS.RECOMMENDED && (
          <Box font-size="small" sx={{ display: 'inline-block' }}>
            <ThumbUpIcon fontSize="small" />
          </Box>
        )}
        {recommendationStatus === RECOMMENDATION_STATUS.NOT_RECOMMENDED && (
          <Box sx={{ display: 'inline-block' }}>
            <ThumbDownIcon fontSize="small" />
          </Box>
        )}
        {isNeutralRating(recommendationStatus) && (
          <Box sx={{ display: 'inline-block' }}>
            <RemoveCircleOutlineIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{ display: 'inline-block', ml: 1 }}>
          <Typography variant="subtitle2">
            {isNeutralRating(recommendationStatus) ? 'Neutral' : recommendationStatus}
          </Typography>
        </Box>
      </Box>
    )
  }
  const getReasonForException = (value: string): string => {
    if (!props?.validReasonsForException) return ''
    const match = props.validReasonsForException.find(([reason]) => reason == value)
    return match?.[1] ?? ''
  }
  const showEditSteerageProviderUI = (providerId: number | null) => {
    handleClose()
    // show the edit form on first click of edit button otherwise hide the form and also hide the create form
    props?.setShowEditProviderForm(showEditProviderForm == providerId ? null : providerId)
    props?.setShowAddProviderForm(false)
  }

  // delete provider from Steerage
  const handleDeleteSteerageProvider = (providerId: number) => {
    handleClose()
    // calling the delete api for deleting provider from the steerage
    const payload = {
      steerage_id: props.steerage.id,
      id: providerId,
    }
    handleDeleteProviderFromSteerage(payload)
      .then(async () => {
        // invalidate queries for fetching the steerage
        await client.cancelQueries({
          queryKey: referralSteerage.retrieveQueryKey({ id: props?.steerage?.id ?? 0 }),
        })
        await client.invalidateQueries({
          queryKey: referralSteerage.retrieveQueryKey({ id: props?.steerage?.id ?? 0 }),
        })
        await client.cancelQueries({
          queryKey: referralSteerageProviders.listQueryKey({
            steerage_id: props?.steerage?.id ?? 0,
          }),
        })
        await client.invalidateQueries({
          queryKey: referralSteerageProviders.listQueryKey({
            steerage_id: props?.steerage?.id ?? 0,
          }),
        })
      })
      // Error notification is already dispatched by useDeleteProviderFromSteerage's
      // { error: '...' } option. Catching here prevents an unhandled rejection when
      // the API fails (mutateAsync re-throws even after onError runs).
      .catch(error => {
        logger.error('[Steerage] delete provider', error)
      })
  }

  const hideAddUpdateProviderForm = () => {
    props?.setShowAddProviderForm(false)
    props?.setShowEditProviderForm(null)
  }

  const handleMemberSelectedProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const providerId = Number(event.target.value)
    props?.setMemberSelectedProvider(providerId)

    // when the providerId is not null then we are selecting a provider
    // else unselecting the steerage provider
    if (providerId) {
      handleSelectingSteerageProvider(
        {
          id: providerId,
          steerage_id: props?.steerage?.id ?? 0,
        },
        {
          onSuccess: () =>
            fireAndForget('[Steerage] refresh after selecting provider', async () => {
              await client.cancelQueries({
                queryKey: referralSteerageProviders.listQueryKey({
                  steerage_id: props?.steerage?.id ?? 0,
                }),
              })
              await client.invalidateQueries({
                queryKey: referralSteerageProviders.listQueryKey({
                  steerage_id: props?.steerage?.id ?? 0,
                }),
              })
              await invalidateCachedCaseData(client, props?.steerage?.person ?? null)
            }),
        },
      )
    }
  }
  const showEditingMenu = !steerage?.is_locked
  return (
    <>
      <Box key={provider.id}>
        <Modal
          open={openAvailabilityModal}
          onClose={(_, reason) => {
            // v9 removed `disableEscapeKeyDown`; declining the
            // escapeKeyDown reason keeps Escape from closing the modal.
            if (reason === 'escapeKeyDown') return
            handleCloseAvailabilityModal()
          }}
          disableScrollLock
          hideBackdrop
        >
          <Box sx={editProviderModalStyle}>
            <UpdateProviderAvailabilityModal
              provider={updateAvailabilityProviderObject()}
              handleCloseAvailabilityModal={() => handleCloseAvailabilityModal()}
              providerSearchType={provider.npi ? 'provider' : 'facility'}
              steerageId={steerage?.id}
              isEditedInDirectory={false}
              specialtyGroups={null}
            />
          </Box>
        </Modal>

        {isEditingWaiverLevel ? <Loader /> : null}
        <br />
        {/* Hide the readonly view of provider when a provider selected for edit*/}
        {showEditProviderForm != provider?.id && (
          <Box>
            <Grid container>
              {/* Only show the radio buttons when it is locked and length of provider is greater then one */}
              {steerage?.is_locked &&
                steerage?.steerage_providers &&
                steerage?.steerage_providers?.length > 1 && (
                  <Grid size={1}>
                    <Box sx={{ mt: -1 }}>
                      <Radio
                        checked={props?.memberSelectedProvider == provider?.id}
                        value={provider?.id}
                        name="radio-buttons"
                        onChange={handleMemberSelectedProviderChange}
                      />
                    </Box>
                  </Grid>
                )}
              <Grid size={steerage?.is_locked ? 11 : 12}>
                {props?.isWaiverToggleEnabled && (
                  <>
                    {steerage?.is_locked &&
                      isMemberInBenefitProgram &&
                      steerage?.waiver?.is_eligible && (
                        <Typography component="div" style={{ marginBottom: '10px' }}>
                          {provider.waiver_level && (
                            <Box sx={{ display: 'inline-block' }}>
                              <img
                                src={waiver_level}
                                alt="Coverage"
                                style={{ width: 'auto', height: '1.3rem', marginRight: '1rem' }}
                              />
                              {SteerageProviderWaiverLevel[provider.waiver_level]}
                            </Box>
                          )}
                        </Typography>
                      )}
                  </>
                )}
                <Box>
                  {!steerage?.is_locked &&
                    isMemberInBenefitProgram &&
                    props?.isWaiverToggleEnabled && (
                      <Grid container>
                        <Grid size={6}>
                          <Box style={{ display: 'flex' }}>
                            <Box style={{ lineHeight: 2.6 }}>Care key:</Box>{' '}
                            <EditField
                              select
                              onChange={e => storeWaiverLevel(e.target.value, provider.id)}
                              value={defaultWaiverLevel}
                              name="waiverLevel"
                              variant="outlined"
                              size="small"
                              style={{ marginBottom: '15px', marginLeft: '10px' }}
                              disabled={isWaiverLevelDisabled(provider)}
                              slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
                            >
                              {Object.entries(SteerageProviderWaiverLevel)
                                ?.filter(([key]) => {
                                  // Only show "Specific Provider" option if NPI is present
                                  // And "Entire Practice" option if careOrganizationName is present
                                  if (
                                    (key === 'Provider' && !provider?.npi) ||
                                    (key === 'Facility' && !provider?.care_organization_name)
                                  ) {
                                    return false
                                  }
                                  return true
                                })
                                ?.map(([key, label]) => (
                                  <option key={label} value={key}>
                                    {label}
                                  </option>
                                ))}
                            </EditField>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  <Grid container>
                    <Grid size={6}>
                      <Box key={provider.id} sx={{ color: 'text.primary' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            mr: 1,
                          }}
                        >
                          <Typography component="div" style={{ display: 'flex' }}>
                            <strong>
                              {provider?.first_name} {provider?.last_name}
                              {/* When a practitioner exists : surface the recommendation status beside the practitioner name */}
                              {(provider?.first_name != null || provider?.last_name != null) &&
                                getRecommendationStatusElementForProvider(provider)}
                            </strong>
                          </Typography>
                        </Box>
                        <Box
                          sx={{ alignItems: 'baseline', justifyContent: 'space-between', mr: 1 }}
                        >
                          <Grid container>
                            <Grid>
                              <Typography>{provider?.care_organization_name}</Typography>
                              {/* When a care-organization exists without a practitioner : surface the recommendation status beside the care-organization name */}
                              {provider?.care_organization_name != null &&
                                provider?.first_name == null &&
                                provider?.last_name == null &&
                                getRecommendationStatusElementForProvider(provider)}
                            </Grid>
                          </Grid>
                        </Box>

                        <Box key={provider.id} sx={{ color: 'text.secondary' }}>
                          {provider?.id && (
                            <Typography>
                              {'Steerage Provider ID '}
                              {provider?.id}
                            </Typography>
                          )}
                          {provider?.npi && (
                            <Typography>
                              {'NPI '}
                              {provider?.npi}
                            </Typography>
                          )}
                          {provider?.stored_tins &&
                            provider?.stored_tins?.length > 0 &&
                            renderTinsWithTruncation(provider.stored_tins)}
                          {provider?.stored_organization_npis &&
                            provider?.stored_organization_npis?.length > 0 &&
                            renderOrganizationNpisWithTruncation(provider.stored_organization_npis)}
                        </Box>

                        {provider?.network_partner_agreement_type && (
                          <Grid container sx={{ alignItems: 'flex-start' }}>
                            <Box sx={{ mt: 1 }}>
                              <PermContactCalendar fontSize="small" />
                            </Box>
                            <Box sx={{ mt: 1, ml: 1 }}>
                              <Typography variant="subtitle2">
                                {getNetworkPartnerDisplayName(
                                  provider?.network_partner_agreement_type,
                                  provider?.partnership?.partnership_type,
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Box>
                    </Grid>
                    <Grid size={5}>
                      {provider?.has_verified_contact_details && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">
                            <VerifiedUserIcon fontSize="small" sx={INLINE_ICON_SX} /> Phone/Fax
                            Verified
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ color: 'text.primary' }} key={provider.id}>
                        {provider?.address_line_1 && (
                          <Typography>{provider?.address_line_1}</Typography>
                        )}
                        {provider?.address_line_2 && (
                          <Typography>{provider?.address_line_2}</Typography>
                        )}
                        <Typography>
                          {provider?.city}
                          {provider?.city && provider?.state && ', '}{' '}
                          {provider?.state?.abbreviation} {provider.zip_code}
                        </Typography>
                        {provider?.phone && <Typography>Ph: {provider?.phone}</Typography>}
                        {provider?.fax && <Typography>Fax: {provider?.fax}</Typography>}
                        {provider?.reason_for_exception && (
                          <Typography>
                            {getReasonForException(provider?.reason_for_exception)}
                          </Typography>
                        )}
                        {/* Three cases
                              1. If availability present and doesProviderExist is true then show the booking out
                              2. doesProviderExist is true then show Availability as not not accepting
                              3. else show the Unknown
                        */}
                        {availability?.days_till_next_availability &&
                        availability?.does_provider_exist != false ? (
                          <Box sx={{ mt: 1 }}>
                            <Typography component="legend">
                              <CalendarTodayIcon sx={INLINE_ICON_SX} />{' '}
                              {'Booking out ' + availability?.days_till_next_availability + ' days'}
                            </Typography>
                            <Typography component="legend">
                              {verifiedOn ? `(verified ${verifiedOn})` : null}
                            </Typography>
                          </Box>
                        ) : availability?.does_provider_exist == false ? (
                          <Box sx={{ mt: 1 }}>
                            <Typography component="legend">
                              <NotInterestedIcon sx={INLINE_ICON_SX} /> Not accepting patients
                            </Typography>
                            {verifiedOn && <Typography>(verified {verifiedOn})</Typography>}
                          </Box>
                        ) : provider?.cpt_code_object ? null : (
                          <Box sx={{ mt: 1 }}>
                            <Button component="span" onClick={handleOpenAvailabilityModal}>
                              + Add availability
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid size={1}>
                      {showEditingMenu && (
                        <IconButton
                          style={{ padding: '0px', marginLeft: '0%' }}
                          data-provider-id={provider.id}
                          onClick={handleMenuClick}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                      {showEditingMenu && (
                        <Menu
                          elevation={1}
                          anchorEl={anchorEl}
                          open={open}
                          onClose={handleClose}
                          slotProps={{
                            list: {
                              disablePadding: true,
                            },
                          }}
                        >
                          {!steerage?.is_locked && (
                            <MenuItem
                              onClick={() => showEditSteerageProviderUI(selectedProviderId)}
                            >
                              Edit provider
                            </MenuItem>
                          )}
                          {!provider?.cpt_code_object && (
                            <MenuItem onClick={() => handleOpenAvailabilityModal()}>
                              Edit availability
                            </MenuItem>
                          )}
                          {!steerage?.is_locked && selectedProviderId && (
                            <MenuItem
                              onClick={() => handleDeleteSteerageProvider(selectedProviderId)}
                            >
                              Remove
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={() => void handleValidateProviderClick()}
                            disabled={isValidating}
                          >
                            {isValidating ? 'Validating...' : 'Validate provider'}
                          </MenuItem>
                        </Menu>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            {props.priorAuthorizationRequired && (
              <Grid container spacing={1} sx={{ alignItems: 'flex-end', mt: 1 }}>
                <Grid size={6}>
                  <Box onBlur={handlePaSystemIdBlur}>
                    <PriorAuthorizationSelect
                      value={paSystemIdInput}
                      onChange={setPaSystemIdInput}
                      onSelectOption={value =>
                        fireAndForget('[Steerage] save prior-auth id', () => savePaSystemId(value))
                      }
                      error={Boolean(paSystemIdError)}
                      helperText={paSystemIdError}
                      disabled={Boolean(steerage?.is_locked)}
                      personId={steerage.person ?? null}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
            <br />
          </Box>
        )}

        {!steerage?.is_locked && showEditProviderForm == provider?.id && (
          <SteerageProviderForm
            steerage={props?.steerage}
            steerageProviderCreateForm={false}
            steerageProvider={provider}
            onCancel={hideAddUpdateProviderForm}
            reasonForExceptionList={props?.validReasonsForException}
            states={states ?? []}
          />
        )}
        {provider?.estimated_cost && provider?.cpt_code_object?.display && (
          <Box sx={{ p: 1, bgcolor: theme => theme.palette.grey[50], mb: 3 }}>
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="body1" sx={{ fontWeight: '500' }}>
                  {provider?.cpt_code_object?.display}
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                size={6}
                sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-end' }}
              >
                <Grid>
                  <Typography variant="body1" component="span">
                    Procedure Est.
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="body1" component="span" sx={{ fontWeight: '500' }}>
                    ${provider?.estimated_cost}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
        <Divider />
      </Box>
      {isSelectingProvider ? <Loader /> : null}

      {/* Validation Result Modal */}
      <Dialog
        open={openValidationModal}
        onClose={handleCloseValidationModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Provider Validation Results</Typography>
            <IconButton onClick={handleCloseValidationModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {isValidating ? (
            <Box>
              {/* Loading skeleton */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="rectangular" width={150} height={32} sx={{ borderRadius: 1 }} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="80%" />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="70%" />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="35%" />
                <Skeleton variant="text" width="60%" />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="25%" />
                <Skeleton variant="text" width="75%" />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 2, textAlign: 'center' }}
              >
                Validating provider information...
              </Typography>
            </Box>
          ) : validationResult?.validation_result?.error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
              <ErrorIcon />
              <Typography>Error: {validationResult.validation_result.error}</Typography>
            </Box>
          ) : validationResult?.validation_result ? (
            <Box>
              {/* Overall Confidence */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {validationResult.validation_result.overall_confidence === 'high' && (
                    <Chip icon={<CheckCircleIcon />} label="High Confidence" color="success" />
                  )}
                  {validationResult.validation_result.overall_confidence === 'medium' && (
                    <Chip icon={<WarningIcon />} label="Medium Confidence" color="warning" />
                  )}
                  {validationResult.validation_result.overall_confidence === 'low' && (
                    <Chip icon={<ErrorIcon />} label="Low Confidence" color="error" />
                  )}
                </Box>
              </Box>

              {/* NPPES Verification */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">NPPES Registry</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {validationResult.validation_result.nppes_verified ? (
                    <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
                  )}
                  <Typography variant="body2">
                    {renderTextWithLinks(validationResult.validation_result.nppes_notes || '')}
                  </Typography>
                </Box>
              </Box>

              {/* Name */}
              {validationResult.validation_result.name_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Name</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {validationResult.validation_result.name_valid ? (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      {renderTextWithLinks(validationResult.validation_result.name_notes)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Address */}
              {validationResult.validation_result.address_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Address</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {validationResult.validation_result.address_valid ? (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      {renderTextWithLinks(validationResult.validation_result.address_notes)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Phone */}
              {validationResult.validation_result.phone_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Phone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {validationResult.validation_result.phone_valid ? (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <WarningIcon color="warning" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      {renderTextWithLinks(validationResult.validation_result.phone_notes)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Fax */}
              {validationResult.validation_result.fax_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Fax</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {validationResult.validation_result.fax_valid ? (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <WarningIcon color="warning" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      {renderTextWithLinks(validationResult.validation_result.fax_notes)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Specialty Service Match */}
              {validationResult.validation_result.specialty_service_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Specialty Match</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {validationResult.validation_result.specialty_service_match === 'high' && (
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    {validationResult.validation_result.specialty_service_match === 'medium' && (
                      <WarningIcon color="warning" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    {validationResult.validation_result.specialty_service_match === 'low' && (
                      <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      {renderTextWithLinks(
                        validationResult.validation_result.specialty_service_notes,
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Recommendations */}
              {validationResult.validation_result.recommendations &&
                validationResult.validation_result.recommendations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Recommendations</Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                      {validationResult.validation_result.recommendations.map((rec, idx) => (
                        <li key={idx}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {renderTextWithLinks(rec.action)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {renderTextWithLinks(rec.justification)}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
            </Box>
          ) : (
            <Typography>No validation results available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SteerageProviderListItem
